import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Preloader from './components/public/Preloader';
import './index.css';

// Public Pages
import HomePage       from './pages/public/HomePage';
import ShopPage       from './pages/public/ShopPage';
import GemDetailPage  from './pages/public/GemDetailPage';
import CartPage       from './pages/public/CartPage';
import CheckoutPage   from './pages/public/CheckoutPage';
import OrderSuccess   from './pages/public/OrderSuccess';
import AboutPage      from './pages/public/AboutPage';
import ContactPage    from './pages/public/ContactPage';

// Admin Pages
import AdminLogin     from './pages/admin/AdminLogin';
import AdminLayout    from './pages/admin/AdminLayout';
import Dashboard      from './pages/admin/Dashboard';
import GemsAdmin      from './pages/admin/GemsAdmin';
import GemForm        from './pages/admin/GemForm';
import OrdersAdmin    from './pages/admin/OrdersAdmin';
import OrderDetail    from './pages/admin/OrderDetail';
import CustomersAdmin from './pages/admin/CustomersAdmin';
import EnquiriesAdmin from './pages/admin/EnquiriesAdmin';
import SeoAdmin       from './pages/admin/SeoAdmin';
import SlidersAdmin   from './pages/admin/SlidersAdmin';
import SettingsAdmin  from './pages/admin/SettingsAdmin';
import AdminUsers        from './pages/admin/AdminUsers';
import CollectionsAdmin from './pages/admin/CollectionsAdmin';

function ProtectedRoute({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'var(--black)' }}>
      <div className="spinner" style={{ width:40, height:40 }} />
    </div>
  );
  return admin ? children : <Navigate to="/admin/login" replace />;
}

export default function App() {
  const [preloading, setPreloading] = useState(() => !sessionStorage.getItem('pl_done'));

  const handlePreloaderDone = () => {
    sessionStorage.setItem('pl_done', '1');
    setPreloading(false);
  };

  return (
    <HelmetProvider>
      {preloading && <Preloader onDone={handlePreloaderDone} />}
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster
              position="top-right"
              toastOptions={{
                style: { background: '#1a1a1a', color: '#F8F5F0', border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'Montserrat, sans-serif', fontSize: '13px' },
                success: { iconTheme: { primary: '#C9A96E', secondary: '#000' } },
                error:   { iconTheme: { primary: '#E05555', secondary: '#000' } },
              }}
            />
            <Routes>
              {/* Public */}
              <Route path="/"             element={<HomePage />} />
              <Route path="/shop"         element={<ShopPage />} />
              <Route path="/gems/:slug"   element={<GemDetailPage />} />
              <Route path="/cart"         element={<CartPage />} />
              <Route path="/checkout"     element={<CheckoutPage />} />
              <Route path="/order-success" element={<OrderSuccess />} />
              <Route path="/about"        element={<AboutPage />} />
              <Route path="/contact"      element={<ContactPage />} />

              {/* Admin */}
              <Route path="/admin/login"  element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index            element={<Dashboard />} />
                <Route path="collections" element={<CollectionsAdmin />} />
                <Route path="gems"      element={<GemsAdmin />} />
                <Route path="gems/new"  element={<GemForm />} />
                <Route path="gems/:id"  element={<GemForm />} />
                <Route path="orders"    element={<OrdersAdmin />} />
                <Route path="orders/:id" element={<OrderDetail />} />
                <Route path="customers" element={<CustomersAdmin />} />
                <Route path="enquiries" element={<EnquiriesAdmin />} />
                <Route path="seo"       element={<SeoAdmin />} />
                <Route path="sliders"   element={<SlidersAdmin />} />
                <Route path="settings"  element={<SettingsAdmin />} />
                <Route path="users"     element={<AdminUsers />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
